import { jsx as _jsx } from "react/jsx-runtime";
import { Video } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getCanvasResolution } from '../../utils/canvasResolution';
export function DirectVideo({ track, onVideoClick, className, }) {
    const mediaStream = useMemo(() => new MediaStream([track]), [track]);
    const videoRef = useRef(null);
    // Diagnostics for the direct output (iOS viewers): the element's media
    // events and the track's state, so a remote participant's log says why
    // its picture stopped (`[VideoElement]` lines, forwarded by peer-diag).
    // A pause that is not ours gets one play() retry: its rejection names
    // the policy (NotAllowedError = autoplay blocked, e.g. Low Power Mode).
    useEffect(() => {
        const video = videoRef.current;
        if (!video)
            return;
        const state = () => ({
            paused: video.paused,
            readyState: video.readyState,
            networkState: video.networkState,
            currentTime: Math.round(video.currentTime * 1000) / 1000,
            size: `${video.videoWidth}x${video.videoHeight}`,
            visibility: document.visibilityState,
            track: `${track.readyState}${track.muted ? '/muted' : ''}${track.enabled ? '' : '/disabled'}`,
        });
        const log = (what, extra) => console.log(`[VideoElement] ${what} ${JSON.stringify({ ...state(), ...extra })}`);
        let retried = false;
        const onPause = () => {
            log('pause');
            if (retried || video.ended)
                return;
            retried = true;
            video.play().then(() => log('play() after pause ok'), (err) => log('play() after pause rejected', {
                error: String(err?.name ?? err),
                message: String(err?.message ?? ''),
            }));
        };
        const onPlaying = () => {
            retried = false;
            log('playing');
        };
        const handlers = [
            ['play', () => log('play')],
            ['playing', onPlaying],
            ['pause', onPause],
            ['waiting', () => log('waiting')],
            ['stalled', () => log('stalled')],
            ['suspend', () => log('suspend')],
            ['emptied', () => log('emptied')],
            ['ended', () => log('ended')],
            ['loadedmetadata', () => log('loadedmetadata')],
            ['resize', () => log('resize')],
            [
                'error',
                () => log('error', {
                    code: video.error?.code,
                    message: video.error?.message,
                }),
            ],
        ];
        for (const [name, handler] of handlers) {
            video.addEventListener(name, handler);
        }
        const trackHandlers = [
            ['mute', () => log('track mute')],
            ['unmute', () => log('track unmute')],
            ['ended', () => log('track ended')],
        ];
        for (const [name, handler] of trackHandlers) {
            track.addEventListener(name, handler);
        }
        const onVisibility = () => log('visibilitychange');
        document.addEventListener('visibilitychange', onVisibility);
        log('mounted');
        const ticker = setInterval(() => {
            if (video.paused || video.readyState < 2)
                log('tick');
        }, 5000);
        return () => {
            clearInterval(ticker);
            document.removeEventListener('visibilitychange', onVisibility);
            for (const [name, handler] of handlers) {
                video.removeEventListener(name, handler);
            }
            for (const [name, handler] of trackHandlers) {
                track.removeEventListener(name, handler);
            }
            log('unmounted');
        };
    }, [track]);
    const handleClick = useCallback((event) => {
        const video = videoRef.current;
        if (!onVideoClick || !video)
            return;
        // the picture is contain-fitted: map the click inside the
        // letterboxed picture to the canvas resolution the scene uses
        const rect = video.getBoundingClientRect();
        const { width: canvasWidth, height: canvasHeight } = getCanvasResolution();
        const videoAspect = video.videoWidth > 0 && video.videoHeight > 0
            ? video.videoWidth / video.videoHeight
            : canvasWidth / canvasHeight;
        const boxAspect = rect.width / rect.height;
        const pictureWidth = videoAspect > boxAspect
            ? rect.width
            : rect.height * videoAspect;
        const pictureHeight = videoAspect > boxAspect
            ? rect.width / videoAspect
            : rect.height;
        const left = rect.left + (rect.width - pictureWidth) / 2;
        const top = rect.top + (rect.height - pictureHeight) / 2;
        const x = ((event.clientX - left) / pictureWidth) * canvasWidth;
        const y = ((event.clientY - top) / pictureHeight) * canvasHeight;
        onVideoClick(x, y);
    }, [onVideoClick]);
    return (_jsx(Video, { srcObject: mediaStream, autoPlay: true, playsInline: true, muted: true, fit: "contain", className: className, innerRef: videoRef, onClick: onVideoClick ? handleClick : undefined }));
}
